import { Injectable, inject } from '@angular/core';
import { CommonPagination, CommonLoading } from '@ceres/frontend-helper';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import {
    ProjectProfile,
    PaginationResponse,
    GetProjectsByFilterWithMissingOfferResponse,
    DIFA_DEV,
    DIFA,
    FiscalYearExport,
    ProjectTree,
    AdjacentInfo,
    ProjectBase,
    TagCategory,
    EmployeeWorkloadStatus
} from '@ceres/domain';
import { Filter } from '@ceres/filter';
import { HttpParams } from '@angular/common/http';
import { catchError, map, switchMap } from 'rxjs/operators';
import { XlsxExportService } from '@ceres/shared/services';
import { TranslateService } from '@ngx-translate/core';
import { ImpersonatedHttpClient } from '@ceres/shared/services';
import { format } from 'date-fns';
import { environment } from '../../../environments/environment';
import { DateService } from '../../shared/services/date.service';
import { ProjectServicesModule } from './project-services.module';

type FiscalYear = { date: Date; dateEnd: Date; label: string };

export interface ProjectConfirmationRequest {
    freetext: string;
    orderValueDelta?: number;
    additionalRecipientGids: string[];
    additionalBusinessPartnerIds?: number[];
    idempotencyKey?: string;
    sendFinancialInformation?: boolean;
}

export interface ProjectConfirmationResult {
    resultingOrderValue: number;
}

@Injectable({
    providedIn: ProjectServicesModule
})
export class ProjectService extends CommonPagination({ index: 0, size: 15 }, CommonLoading(Object)) {
    private http = inject(ImpersonatedHttpClient);
    private dateService = inject(DateService);
    private xlsxExportService = inject(XlsxExportService);
    private translateService = inject(TranslateService);

    private _projectProfiles: BehaviorSubject<ProjectProfile[]> = new BehaviorSubject<ProjectProfile[]>([]);
    public projectProfiles$: Observable<ProjectProfile[]> = this._projectProfiles.asObservable();

    public allFiscalYears: FiscalYear[];

    private _appliedFilters: Filter[] = [];

    constructor() {
        super();
        this.allFiscalYears = this.dateService.getBusinessYears();
    }

    public set appliedFilters(appliedFilters: Filter[]) {
        this._appliedFilters = appliedFilters;
        this.pageIndex = 0;
        this.getAll();
    }

    public get appliedFilters(): Filter[] {
        return this._appliedFilters;
    }

    public getProjectAdjacentInfo(projectId: number) {
        return this.http.get<AdjacentInfo>(`${environment.edgeService}/projects/projectAdjacentInfo/${projectId}`);
    }

    public confirmProject(
        projectId: number,
        payload: ProjectConfirmationRequest
    ): Observable<ProjectConfirmationResult> {
        return this.http.post<ProjectConfirmationResult>(
            `${environment.edgeService}/projects/${projectId}/confirmation`,
            payload
        );
    }

    public getProjectCopiedFrom(projectId: number): Observable<ProjectBase> {
        return this.http.get<ProjectBase>(`${environment.edgeService}/projects/projectCopiedFrom/${projectId}`);
    }

    public getProjectTree(projectId: number) {
        return this.http.get<ProjectTree[]>(`${environment.edgeService}/projects/projectTree/${projectId}`);
    }

    public checkEmployeeWorkload(projectId: number, employeeIds: number[]): Observable<EmployeeWorkloadStatus[]> {
        if (!employeeIds.length) {
            return of([]);
        }
        return this.http.post<EmployeeWorkloadStatus[]>(
            `${environment.edgeService}/projects/profiles/${projectId}/employees/workload-status`,
            { employeeIds }
        );
    }

    public getMissingLinkProjects() {
        return this.http.get(`${environment.edgeService}/projects/profilesMissingLink`);
    }

    public getAccountingDistribute(projectId: number) {
        return this.http.get(`${environment.edgeService}/projects/${projectId}/accounting/distribute`);
    }

    public getMaterials() {
        return this.http.get(`${environment.edgeService}/projects/profiles/materials`);
    }

    public getProjectProfile(projectId: number) {
        return this.http.get(`${environment.edgeService}/projects/profiles/${projectId}`);
    }

    public getProjectProfileFinancialOverview(projectId: number) {
        return this.http.get(`${environment.edgeService}/projects/financials/overview/${projectId}`);
    }

    public setParent(projectId: number, parentId: number) {
        return this.http.put(`${environment.edgeService}/projects/setParent/${projectId}`, {
            parentId
        });
    }

    public putAccountingDistribute(data) {
        return this.http.put(`${environment.edgeService}/projects/accounting/infos/distribute`, data);
    }

    public getAll() {
        this.loading = true;
        let params = this.getPaginationParams();
        params = this.appendFilters(params);

        this.http
            .get<PaginationResponse<ProjectProfile>>(`${environment.edgeService}/projects/profiles`, {
                params
            })
            .pipe(
                catchError(() => of({ data: [] as ProjectProfile[], total: 0 })),
                map(({ data, total }) => {
                    const results = this.prepareProjectData(data);
                    return { total, data: results };
                })
            )
            .subscribe(({ data, total }) => {
                this._total.next(total);
                this._projectProfiles.next(data);
                this.loading = false;
            });
    }

    public async exportProjects(displayedColumns: string[], hasInheritance: boolean): Promise<any> {
        const data = await this.fetchAndCombineDailyData();

        if (typeof data === 'boolean') {
            return true;
        }

        // Use aggregated Total values for export so parent projects show correct sums.
        // For tenants without project value inheritance, *Total equals the project's own value.
        data.forEach((project) => {
            project.remainingBudget = project.remainingBudgetTotal;
            project.valueSettledByHours = project.valueSettledByHoursTotal;
            project.proposedBillingHours = project.proposedBillingHoursTotal;
            project.proposedBillingValueHours = project.proposedBillingValueHoursTotal;
            project.hoursSettled = project.hoursSettledTotal;
            project.linearForecast = project.linearForecastTotal;
        });

        if (displayedColumns.includes('linearForecastPercentage')) {
            data.forEach((project) => {
                project.linearForecastPercentage = this.calculateLinearForecastPercentage(project, hasInheritance);
            });
        }

        return this.xlsxExportService.genericExport(
            this.translateService.instant('project.list.excel.file'),
            data,
            displayedColumns,
            ''
        );
    }

    private calculateLinearForecastPercentage(project: ProjectProfile, hasInheritance: boolean) {
        if (!project) {
            return 0;
        }
        const projectTypeName = project.projectType?.name as string;
        let orderValue = 0;
        if (hasInheritance) {
            orderValue = projectTypeName === 'ChildProject' ? (project.auftragswert ?? 0) : (project.bestellwert ?? 0);
        } else {
            orderValue = project.auftragswert ?? 0;
        }
        return orderValue ? Math.round((project.linearForecast / orderValue) * 10000) / 100 : 0;
    }

    private getAllProjects(pageIndex: number, pageSize: number): Promise<ProjectProfile[]> {
        let params = this.setPaginationParams(pageIndex, pageSize);
        params = this.appendFilters(params);

        return this.http
            .get<PaginationResponse<ProjectProfile>>(`${environment.edgeService}/projects/profiles`, {
                params
            })
            .pipe(
                catchError(() => of({ data: [] as ProjectProfile[], total: 0 })),
                map(({ data }) =>
                    this.prepareProjectData(data).map((element) => {
                        this.translateForExcellExport(element);
                        return element;
                    })
                )
            )
            .toPromise();
    }

    private async fetchAndCombineDailyData(): Promise<ProjectProfile[] | boolean> {
        const pageSize = this.appliedFilters.some((filter) => filter.key === 'projektGruppierung') ? 50 : 300;
        let totalNumberOfData: number = this._total.value;

        try {
            if (!totalNumberOfData) {
                totalNumberOfData = await this.getProjectsTotal();
            }
        } catch (_) {
            return true;
        }

        const iterations = Math.ceil(totalNumberOfData / pageSize);

        const results = [];
        for (let index = 0; index < iterations; index++) {
            const result = await this.getAllProjects(index, pageSize);
            results.push(result);
        }
        return results.reduce((acc, result) => acc.concat(result), []);
    }

    /**
     * Gets the amount of the data that should be exported for the projects.
     * @returns number of the rows in the Excel table, that should be displayed
     * @throws an Error if the amount of data is 0 or if some Error happened in the backend
     */
    private async getProjectsTotal(): Promise<number> {
        try {
            let params = new HttpParams();
            params = this.appendFilters(params);

            const result: number = await this.http
                .get<number>(`${environment.edgeService}/projects/profiles-total`, {
                    params
                })
                .toPromise();

            if (result === 0) {
                throw new Error();
            }
            return result;
        } catch (_) {
            return Promise.reject(new Error('No data to export'));
        }
    }

    private translateForExcellExport(element: ProjectProfile) {
        element.verrechnungsart = this.translateService.instant(
            `projects.project-property-options.accounting-type.${element.verrechnungsart}`
        );
        element.verrechnungszyklus = this.translateService.instant(
            `projects.project-property-options.accounting-cycle.${element.verrechnungszyklus}`
        );
        element.projectStatus = this.translateService.instant(element.projectStatus);
        const tagArray = Array.isArray(element.projectTags)
            ? element.projectTags.map((tag) => this.translateService.instant(tag.translationKey))
            : ['-'];
        const tagString = tagArray.join(', ');
        Object.assign(element, { projectTagsForExport: tagString });

        const cat1Tags = Array.isArray(element.projectTags)
            ? element.projectTags.filter((t) => t.category === TagCategory.Category1)
            : [];
        const cat2Tags = Array.isArray(element.projectTags)
            ? element.projectTags.filter((t) => t.category === TagCategory.Category2)
            : [];
        Object.assign(element, {
            projectTagsCategory1ForExport:
                cat1Tags.map((t) => this.translateService.instant(t.translationKey)).join(', ') || '-',
            projectTagsCategory2ForExport:
                cat2Tags.map((t) => this.translateService.instant(t.translationKey)).join(', ') || '-'
        });
    }

    public exportProjectsWithMissingOrderNumber(): Observable<any> {
        let params = new HttpParams();
        params = this.appendFilters(params);

        let displayedColumns = [
            'id',
            'mpNumber',
            'mpTitle',
            'businessPartner.salutation',
            'businessPartner.firstName',
            'businessPartner.lastName',
            'businessPartner.email',
            'businessPartner.department',
            'businessPartner.gid',
            'projectLead.name',
            'projectLead.email',
            'projectLead.gid',
            'offerTitle',
            'offerNumber',
            'offerId'
        ];

        if (environment.tenant === DIFA_DEV || environment.tenant === DIFA) {
            displayedColumns = displayedColumns.concat('orgId', 'sdNumber', 'orderNumber');
        }

        return this.http
            .get<PaginationResponse<GetProjectsByFilterWithMissingOfferResponse>>(
                `${environment.edgeService}/projects/profilesMissingOffer`,
                {
                    params
                }
            )
            .pipe(
                catchError(() =>
                    of({
                        data: [] as GetProjectsByFilterWithMissingOfferResponse[],
                        total: 0
                    })
                ),
                map(({ data }) => data),
                switchMap((data: GetProjectsByFilterWithMissingOfferResponse[]): Observable<boolean | void> =>
                    data.length === 0
                        ? of(true)
                        : from(
                              this.xlsxExportService.genericExport(
                                  this.translateService.instant('project.list.excel.file'),
                                  data,
                                  displayedColumns
                              )
                          )
                )
            );
    }

    public fiscalYearExport(currentFiscalYear: {
        date: Date;
        dateEnd: Date;
        label: string;
    }): Observable<boolean | void> {
        const params = new HttpParams()
            .set('currentFiscalYearStartDate', format(currentFiscalYear.date, 'yyyy-MM-dd'))
            .set('currentFiscalYearEndDate', format(currentFiscalYear.dateEnd, 'yyyy-MM-dd'));

        const displayedColumns = [
            'costCenterDepartment',
            'additionalActivityDescription',
            'projectPspElement',
            'workingHoursSum'
        ];

        const columnsToAdd = [
            { key: 'ME', index: displayedColumns.indexOf('workingHoursSum') + 1 },
            { key: 'PersNr', index: displayedColumns.indexOf('workingHoursSum') + 2 },
            { key: 'Text', index: displayedColumns.indexOf('workingHoursSum') + 3 }
        ];

        return this.http
            .get<FiscalYearExport[]>(`${environment.edgeService}/time-sheets/fiscal-year-export`, { params })
            .pipe(
                catchError(() => of([])),
                switchMap((data: FiscalYearExport[]): Observable<boolean | void> =>
                    data.length === 0
                        ? of(true)
                        : from(
                              this.xlsxExportService.genericExport(
                                  this.translateService.instant('project.list.excel.fiscalYearFile', {
                                      fiscalYear: currentFiscalYear.label,
                                      currentDate: format(new Date(), 'yyyy-MM-dd')
                                  }),
                                  data,
                                  displayedColumns,
                                  '',
                                  columnsToAdd
                              )
                          )
                )
            );
    }

    private appendFilters(params: HttpParams) {
        if (this.appliedFilters.length > 0) {
            return params.append('filters', JSON.stringify(this.appliedFilters));
        }
        return params;
    }

    private calculateDays() {
        const date = new Date();
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString();
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString();
        return { firstDay, lastDay };
    }

    private prepareProjectData(data: ProjectProfile[]) {
        const { firstDay, lastDay } = this.calculateDays();
        return data.map((element) => {
            if (!element.validFrom) {
                element.validFrom = firstDay;
            }
            if (!element.validTo) {
                element.validTo = lastDay;
            }
            if (!element.istpreisExtern) {
                element.istpreisExtern = 0;
            }

            for (const elem in element) {
                if (!isNaN(element[elem])) {
                    element[elem] = element[elem] ? +(+element[elem]).toFixed(2) : 0;
                }
            }
            return element;
        });
    }

    private setPaginationParams(pageIndex: number, pageSize: number): HttpParams {
        let params = new HttpParams();
        params = params.append('page', `${pageIndex}`);
        params = params.append('size', `${pageSize}`);
        if (this.pageSort && this.pageSort.key && this.pageSort.direction) {
            params = params.append('sort', `${this.pageSort.key},${this.pageSort.direction.toUpperCase()}`);
        }
        return params;
    }
}
